import React, { useState } from 'react';
import AttributesList from '../components/settings/AttributesList';
import ObligationsList from '../components/settings/ObligationsList';
import AddAttributeForm from '../components/settings/AddAttributeForm';
import AddObligationForm from '../components/settings/AddObligationForm';
import ObligationExample from '../components/settings/ObligationExample';
import { PlusIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
// Mock data for attributes
const initialAttributes = [{
  id: 1,
  name: 'Pool',
  count: 5,
  dataType: 'Number'
}, {
  id: 2,
  name: 'Grill',
  count: 10,
  dataType: 'Number'
}, {
  id: 3,
  name: 'Elevator',
  count: 2,
  dataType: 'Text'
}, {
  id: 4,
  name: 'Construction Date',
  count: 1,
  dataType: 'Date'
}, {
  id: 5,
  name: 'Rooftop Access',
  count: 3,
  dataType: 'List: Outside Access, Inside Access'
}, {
  id: 6,
  name: 'Security System',
  count: 8,
  dataType: 'Yes/No'
}];
// Mock data for obligations
const initialObligations = [{
  id: 1,
  name: 'Pool Permit',
  description: 'Annual permit required for all pools',
  attributeId: 1,
  dueDate: '2023-12-31',
  recurring: 'Annually',
  responsibleParty: 'On-Site Maintenance'
}, {
  id: 2,
  name: 'BBQ Inspection',
  description: 'Safety inspection for all BBQ grills',
  attributeId: 2,
  dueDate: '2023-10-15',
  recurring: 'Annually',
  responsibleParty: 'Tenant',
  attributeConditions: [{
    attributeId: 2,
    operator: 'Does Not Equal',
    value: '0'
  }]
}, {
  id: 3,
  name: 'Elevator Maintenance',
  description: 'Regular maintenance check required by law',
  attributeId: 3,
  dueDate: '2023-09-30',
  recurring: 'Quarterly',
  responsibleParty: 'Outsourced to Vendor'
}, {
  id: 4,
  name: 'Water Tank Cleaning',
  description: 'Required for NYC properties',
  attributeId: null,
  location: 'New York City',
  dueDate: '2023-11-15',
  recurring: 'Annually',
  responsibleParty: 'Corporate Team',
  propertyFields: [{
    field: 'Ownership Group',
    operator: 'Equals',
    value: 'Greystar'
  }],
  locationFields: ['New York City']
}];
const SettingsPage = () => {
  const [attributes, setAttributes] = useState(initialAttributes);
  const [obligations, setObligations] = useState(initialObligations);
  const [showAddAttribute, setShowAddAttribute] = useState(false);
  const [showAddObligation, setShowAddObligation] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [editingAttributeId, setEditingAttributeId] = useState(null);
  const [editingObligationId, setEditingObligationId] = useState(null);
  const handleAddAttribute = newAttribute => {
    setAttributes([...attributes, {
      id: attributes.length + 1,
      ...newAttribute
    }]);
    setShowAddAttribute(false);
  };
  const handleAddObligation = newObligation => {
    setObligations([...obligations, {
      id: obligations.length + 1,
      ...newObligation
    }]);
    setShowAddObligation(false);
  };
  const handleEditAttribute = attributeId => {
    setEditingAttributeId(attributeId);
    setShowAddAttribute(false);
  };
  const handleEditObligation = obligationId => {
    setEditingObligationId(obligationId);
    setShowAddObligation(false);
  };
  // Check if we're in any editing or adding mode
  const isEditingOrAdding = showAddAttribute || showAddObligation || editingAttributeId || editingObligationId;
  return <div className="min-h-screen bg-gray-50" data-id="element-385">
      <header className="bg-white shadow" data-id="element-386">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-387">
          <div className="flex justify-between items-center" data-id="element-388">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-389">
              Company Settings
            </h1>
            <div className="flex space-x-3" data-id="element-390">
              <Link to="/" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-opacity" style={{
              backgroundColor: '#332275'
            }} data-id="element-391">
                Reports
              </Link>
              <Link to="/property-details" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-opacity" style={{
              backgroundColor: '#332275'
            }} data-id="element-392">
                View Property Details
              </Link>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-393">
        {/* Example section - only shown when button is clicked */}
        {showExample && <div className="mb-8" data-id="element-394">
            <div className="flex justify-between items-center mb-4" data-id="element-395">
              <h2 className="text-xl font-semibold text-gray-800" data-id="element-396">
                How to Add an Obligation
              </h2>
              <button onClick={() => setShowExample(false)} className="text-gray-500 hover:text-gray-700" data-id="element-397">
                <XIcon className="h-5 w-5" data-id="element-398" />
              </button>
            </div>
            <ObligationExample data-id="element-399" />
          </div>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-400">
          {/* Attributes Section */}
          <div className="bg-white shadow rounded-lg p-6" data-id="element-401">
            <div className="flex justify-between items-center mb-2" data-id="element-402">
              <h2 className="text-xl font-semibold text-gray-800" data-id="element-403">
                Property Attributes
              </h2>
              {!isEditingOrAdding && <button onClick={() => setShowAddAttribute(true)} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-opacity" style={{
              backgroundColor: '#332275'
            }} data-id="element-404">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-405" />
                  Add Attribute
                </button>}
            </div>
            <p className="text-sm text-gray-600 mb-6" data-id="element-406">
              Every property will be able to fill in these attributes by
              default. Each property may also add their own custom attributes.
            </p>
            {showAddAttribute ? <AddAttributeForm onSubmit={handleAddAttribute} onCancel={() => setShowAddAttribute(false)} data-id="element-407" /> : editingAttributeId ? <AddAttributeForm onSubmit={updatedAttribute => {
            // In a real app, this would update the attribute
            // For now just close the form
            setEditingAttributeId(null);
          }} onCancel={() => setEditingAttributeId(null)} initialData={attributes.find(attr => attr.id === editingAttributeId)} data-id="element-408" /> : <AttributesList attributes={attributes} onEdit={handleEditAttribute} data-id="element-409" />}
          </div>
          {/* Obligations Section */}
          <div className="bg-white shadow rounded-lg p-6" data-id="element-410">
            <div className="flex justify-between items-center mb-2" data-id="element-411">
              <h2 className="text-xl font-semibold text-gray-800" data-id="element-412">
                Maintenance Requirements
              </h2>
              {!isEditingOrAdding && <div className="flex space-x-2" data-id="element-413">
                  <button onClick={() => setShowAddObligation(true)} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-opacity" style={{
                backgroundColor: '#332275'
              }} data-id="element-414">
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-415" />
                    Add Requirement
                  </button>
                </div>}
            </div>
            <p className="text-sm text-gray-600 mb-6" data-id="element-416">
              These obligations will automatically apply to properties based on
              their attributes or location. Each property can also have custom
              obligations.
            </p>
            {showAddObligation ? <AddObligationForm onSubmit={handleAddObligation} onCancel={() => setShowAddObligation(false)} attributes={attributes} data-id="element-417" /> : editingObligationId ? <AddObligationForm onSubmit={updatedObligation => {
            // In a real app, this would update the obligation
            // For now just close the form
            setEditingObligationId(null);
          }} onCancel={() => setEditingObligationId(null)} attributes={attributes} initialData={obligations.find(obl => obl.id === editingObligationId)} data-id="element-418" /> : <ObligationsList obligations={obligations} attributes={attributes} onEdit={handleEditObligation} data-id="element-419" />}
          </div>
        </div>
      </main>
    </div>;
};
export default SettingsPage;